'use client';

import { useEffect, useRef } from 'react';
import { playMediaSafely } from '@/app/utils/client';

interface UseHoverPlayVideoOptions {
  delay?: number;
  resetOnLeave?: boolean;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onPlayStart?: () => void;
}

interface UseHoverPlayVideoReturn {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

export const useHoverPlayVideo = ({
  delay = 0,
  resetOnLeave = false,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
  onPlayStart,
}: UseHoverPlayVideoOptions = {}): UseHoverPlayVideoReturn => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingTimer = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const handleMouseEnter = () => {
    if (disabled) return;

    onMouseEnter?.();
    clearPendingTimer();

    timerRef.current = setTimeout(() => {
      playMediaSafely(videoRef.current);
      onPlayStart?.();
    }, delay);
  };

  const handleMouseLeave = () => {
    if (disabled) return;

    onMouseLeave?.();
    clearPendingTimer();

    if (!videoRef.current) return;
    videoRef.current.pause();
    if (resetOnLeave) {
      videoRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    return () => {
      clearPendingTimer();
      if (!videoRef.current) return;
      videoRef.current.pause();
      if (resetOnLeave) {
        videoRef.current.currentTime = 0;
      }
    };
  }, [resetOnLeave]);

  return {
    videoRef,
    handleMouseEnter,
    handleMouseLeave,
  };
};
